import React, { useState, useEffect } from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Typography,
  Space,
  Divider,
  Button,
  Table,
  Alert,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  TeamOutlined,
  CalculatorOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../../context/AuthContext";
import { useSelection } from "../../../SelectionContext";
import useBusinessResource from "../../../hooks/useBusinessResource";
import {
  PayrollPeriod,
  PayrollEmployee,
  PayrollStatistics,
} from "../../../types/hr";
import {
  formatCurrency,
  formatDate,
  formatPeriod,
  getDaysBetween,
  completedPayrollCount,
} from "../utils";
import PayslipDetailsModal from "./PayslipDetailsModal";

const { Title, Text } = Typography;

interface PeriodDetailsModalProps {
  visible: boolean;
  period: PayrollPeriod | null;
  onCancel: () => void;
}

// Composant pour afficher le statut avec couleur appropriée
const isValidationInProgress = (
  period: PayrollPeriod,
  statistics?: PayrollStatistics | null
) => {
  const totalCalculations = Number(
    period.total_calculations ||
      statistics?.total_calculations ||
      period.total_employees ||
      statistics?.total_employees ||
      0
  );
  const validatedCalculations = Number(
    period.validated_calculations || statistics?.validated_calculations || 0
  );

  return (
    period.status === "calculated" &&
    validatedCalculations > 0 &&
    validatedCalculations < totalCalculations
  );
};

const StatusTag: React.FC<{
  period: PayrollPeriod;
  statistics?: PayrollStatistics | null;
}> = ({ period, statistics }) => {
  const statusConfig = {
    draft: { color: "default", text: "Brouillon" },
    variables_set: { color: "blue", text: "Pret pour calcul" },
    processing: { color: "processing", text: "En Traitement" },
    calculated: { color: "warning", text: "Calculé" },
    partial_error: { color: "error", text: "Erreurs Partielles" },
    validated: { color: "success", text: "Validé" },
    closed: { color: "default", text: "Fermé" },
  };

  if (isValidationInProgress(period, statistics)) {
    return <Tag color="processing">Validation en cours</Tag>;
  }

  const config =
    statusConfig[period.status as keyof typeof statusConfig] ||
    statusConfig.draft;
  return <Tag color={config.color}>{config.text}</Tag>;
};

const PeriodDetailsModal: React.FC<PeriodDetailsModalProps> = ({
  visible,
  period,
  onCancel,
}) => {
  const { user, token } = useAuth();
  const { selectedCompany } = useSelection();
  const [employees, setEmployees] = useState<PayrollEmployee[]>([]);
  const [statistics, setStatistics] = useState<PayrollStatistics | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null
  );

  const resource = useBusinessResource({
    userId: user?.id, companyId: period?.company_id, warehouseId: "company", moduleScope: "hr",
    resource: "payroll-period-details", parameters: [period?.id], active: visible,
    enabled: Boolean(visible && period && token && Number(period.company_id) === Number(selectedCompany)),
    load: async signal => {
      if (!period) throw new Error("Période manquante");
      const read = async (suffix: string) => {
        const response = await fetch(`/api/hr/payroll-processing/periods/${period.id}/${suffix}`, {
          signal, headers: { Authorization: `Bearer ${token}`, "X-Company-Id": String(period.company_id) },
        });
        const result = await response.json();
        if (!response.ok || !result.success || result.data == null) throw new Error(result.error || result.message || "Lecture de la période impossible");
        return result.data;
      };
      const [employees, statistics] = await Promise.all([read('employees'), read('statistics')]);
      if (!Array.isArray(employees) || typeof statistics !== 'object') throw new Error("Réponse de période invalide");
      if (statistics.period?.id != null && Number(statistics.period.id) !== Number(period.id)) throw new Error("Statistiques reçues pour une autre période");
      return { employees: employees as PayrollEmployee[], statistics: { ...(statistics.overall || statistics), period_id: period.id } as PayrollStatistics };
    },
  });
  useEffect(() => {
    setEmployees([]); setStatistics(null); setDataError(null); setShowPayslipModal(false); setSelectedEmployeeId(null);
  }, [visible, period?.id, period?.company_id, selectedCompany]);
  useEffect(() => {
    if (!visible || !period) return;
    setLoading(resource.loading);
    if (Number(period.company_id) !== Number(selectedCompany)) { setDataError("L’entreprise sélectionnée a changé. Rouvrez les détails."); return; }
    if (resource.error) { setDataError(resource.error instanceof Error ? resource.error.message : "Lecture de la période impossible"); return; }
    if (resource.status === 'verified' && resource.data) {
      setEmployees(resource.data.employees); setStatistics(resource.data.statistics); setDataError(null);
    }
  }, [visible, selectedCompany, resource.data, resource.status, resource.error, resource.loading]);
  const loadPeriodData = async () => {
    try { await resource.refresh({ force: true }); }
    catch (error) { setDataError(error instanceof Error ? error.message : "Lecture de la période impossible"); }
  };

  if (!period) return null;

  // Vérification de sécurité pour éviter les erreurs de rendu
  if (!period.id || !period.name || !period.start_date || !period.end_date) {
    return (
      <Modal
        title="Erreur"
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>
            Fermer
          </Button>,
        ]}
      >
        <Alert
          message="Données de période incomplètes"
          description="Les données de la période ne sont pas complètes. Veuillez rafraîchir la page et réessayer."
          type="error"
          showIcon
        />
      </Modal>
    );
  }

  const getProgressInfo = () => {
    const total = Number(statistics?.total_employees ?? period.total_employees ?? 0);
    const completed = completedPayrollCount(statistics || period);
    const errors = statistics?.error_calculations ?? period?.error_calculations ?? 0;

    if (total === 0) return { percent: 0, status: "normal" as const };

    const percent = Math.round((completed / total) * 100);
    let status: "success" | "active" | "exception" | "normal" = "normal";

    if (errors > 0) status = "exception";
    else if (completed === total) status = "success";
    else if (completed > 0) status = "active";

    return { percent, status };
  };

  const progressInfo = getProgressInfo();
  const periodDuration =
    period?.start_date && period?.end_date
      ? getDaysBetween(period.start_date, period.end_date)
      : 0;

  const employeeColumns = [
    {
      title: "Employé",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (name: string, record: PayrollEmployee) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.employee_number}
          </Text>
        </div>
      ),
    },
    {
      title: "Département",
      dataIndex: "department_name",
      key: "department",
      render: (dept: string) => dept || "-",
    },
    {
      title: "Poste",
      dataIndex: "position_name",
      key: "position",
      render: (pos: string) => pos || "-",
    },
    {
      title: "Statut Calcul",
      dataIndex: "calculation_status",
      key: "calc_status",
      render: (status: string) => {
        const statusConfig = {
          pending: { color: "default", text: "En Attente" },
          processing: { color: "processing", text: "En Cours" },
          completed: { color: "success", text: "Terminé" },
          validated: { color: "success", text: "Validé" },
          error: { color: "error", text: "Erreur" },
        };
        const config =
          statusConfig[status as keyof typeof statusConfig] ||
          statusConfig.pending;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Salaire Brut",
      dataIndex: "gross_salary",
      key: "gross",
      render: (amount: number) => (
        <Text strong style={{ color: amount > 0 ? "#52c41a" : undefined }}>
          {amount > 0 ? formatCurrency(amount) : "-"}
        </Text>
      ),
    },
    {
      title: "Salaire Net",
      dataIndex: "net_salary",
      key: "net",
      render: (amount: number) => (
        <Text strong style={{ color: amount > 0 ? "#1890ff" : undefined }}>
          {amount > 0 ? formatCurrency(amount) : "-"}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: PayrollEmployee) => (
        <Space>
          <Tooltip title="Voir le bulletin IUTS">
            <Button
              type="text"
              icon={<FileTextOutlined />}
              size="small"
              disabled={!record.is_calculated}
              onClick={() => handleViewPayslip(record.employee_id)}
              style={{ color: record.is_calculated ? "#1890ff" : undefined }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleViewPayslip = (employeeId: number) => {
    setSelectedEmployeeId(employeeId);
    setShowPayslipModal(true);
  };

  const handleClosePayslip = () => {
    setShowPayslipModal(false);
    setSelectedEmployeeId(null);
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Détails de la Période
            </Title>
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        width={1000}
        footer={[
          <Button key="close" onClick={onCancel}>
            Fermer
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              // TODO: Implement export functionality
              console.log("Export period:", period?.id);
            }}
          >
            Exporter
          </Button>,
        ]}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {dataError && <Alert type="error" showIcon message="Les détails de cette période ne sont pas vérifiés" description={dataError}
            action={<Button onClick={loadPeriodData}>Actualiser</Button>} style={{ marginBottom: 16 }} />}
          {loading && <Alert type="info" message="Actualisation des données de cette période…" style={{ marginBottom: 16 }} />}
          {/* Informations générales */}
          <Card title="Informations Générales" style={{ marginBottom: 16 }}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Nom de la Période" span={2}>
                <Text strong>{period?.name || "Non défini"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Période">
                <Space>
                  <CalendarOutlined />
                  {period?.start_date && period?.end_date
                    ? formatPeriod(period.start_date, period.end_date)
                    : "Non défini"}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Durée">
                {periodDuration} jour{periodDuration > 1 ? "s" : ""}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <StatusTag period={period} statistics={statistics} />
              </Descriptions.Item>
              <Descriptions.Item label="Créé le">
                {period?.created_at
                  ? formatDate(period.created_at, "long")
                  : "Non défini"}
              </Descriptions.Item>
              {period?.description && (
                <Descriptions.Item label="Description" span={2}>
                  {period.description}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Statistiques */}
          <Card 
            title="Statistiques" 
            style={{ marginBottom: 16 }}
            loading={loading}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Employés"
                  value={statistics?.total_employees ?? period?.total_employees ?? 0}
                  prefix={<TeamOutlined style={{ color: "#1890ff" }} />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Calculs Terminés"
                  value={completedPayrollCount(statistics || period)}
                  suffix={`/ ${statistics?.total_employees ?? period?.total_employees ?? 0}`}
                  prefix={<CalculatorOutlined style={{ color: "#52c41a" }} />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Masse Salariale Brute"
                  value={statistics?.total_gross_salary ?? period?.total_gross_salary ?? 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<DollarOutlined style={{ color: "#722ed1" }} />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Masse Salariale Nette"
                  value={statistics?.total_net_salary ?? period?.total_net_salary ?? 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<DollarOutlined style={{ color: "#fa8c16" }} />}
                  valueStyle={{ color: "#fa8c16" }}
                />
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Progression des Calculs</Text>
                <Progress
                  percent={progressInfo.percent}
                  status={progressInfo.status}
                  format={(percent) =>
                    `${completedPayrollCount(statistics || period)}/${
                      statistics?.total_employees ?? period?.total_employees ?? 0
                    }`
                  }
                  style={{ marginTop: 8 }}
                />
              </Col>
              <Col span={12}>
                {(statistics?.error_calculations ?? period?.error_calculations ?? 0) > 0 && (
                  <Alert
                    message={`${statistics?.error_calculations ?? period?.error_calculations ?? 0} erreur(s) de calcul`}
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginTop: 8 }}
                  />
                )}
                {progressInfo.percent === 100 &&
                  (statistics?.error_calculations ?? period?.error_calculations ?? 0) === 0 && (
                    <Alert
                      message="Tous les calculs sont terminés"
                      type="success"
                      showIcon
                      icon={<CheckCircleOutlined />}
                      style={{ marginTop: 8 }}
                    />
                  )}
              </Col>
            </Row>
          </Card>

          {/* Liste des employés */}
          <Card title="Employés de la Période" style={{ marginBottom: 16 }}>
            <Table
              columns={employeeColumns}
              dataSource={employees}
              loading={loading}
              rowKey="employee_id"
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} sur ${total} employés`,
              }}
              scroll={{ x: 800 }}
            />
          </Card>

          {/* Alertes selon le statut */}
          {period?.status === "draft" && (
            <Alert
              message="Période en Brouillon"
              description="Cette période est en cours de préparation. Vous pouvez ajouter des employés et définir les variables de paie."
              type="info"
              showIcon
            />
          )}

          {period?.status === "processing" && (
            <Alert
              message="Calculs en Cours"
              description="Les calculs de paie sont en cours d'exécution. Veuillez patienter."
              type="warning"
              showIcon
            />
          )}

          {period?.status === "calculated" && (
            <Alert
              message="Calculs Terminés"
              description="Les calculs sont terminés. Vous pouvez maintenant valider les résultats et générer les bulletins."
              type="success"
              showIcon
            />
          )}

          {period?.status === "validated" && (
            <Alert
              message="Période Validée"
              description="Cette période a été validée. Vous pouvez générer les bulletins de paie."
              type="success"
              showIcon
            />
          )}

          {period?.status === "closed" && (
            <Alert
              message="Période Fermée"
              description="Cette période est fermée et archivée. Aucune modification n'est possible."
              type="info"
              showIcon
            />
          )}
        </div>
      </Modal>

      {/* Modal de bulletin de paie IUTS */}
      <PayslipDetailsModal
        visible={showPayslipModal}
        employeeId={selectedEmployeeId}
        period={period}
        onCancel={handleClosePayslip}
      />
    </>
  );
};

export default PeriodDetailsModal;
